import { formatMoney, type Money } from "@wakaskul/shared";
import {
  Calendar,
  CalendarClock,
  Check,
  CircleDollarSign,
  GraduationCap,
  Layers3,
  LayoutDashboard,
  MessageSquare,
  Settings2,
  UserCog,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

import {
  Eyebrow,
  PrimaryAction,
  SecondaryAction,
  SectionHead,
  SitePage,
} from "./site/SiteChrome";
import { OfficialWordmark } from "../components/OfficialWordmark";

/**
 * The public face of the product, shown at "/" before anyone signs in.
 *
 * Structured to sit beside Waka ERP — same company, same shape of page: a
 * short headline, a screen of the real software, then more screens. The copy
 * stays short on purpose. A director scrolling this page decides from what the
 * software looks like doing their job, not from paragraphs about it, so almost
 * every block here is a mockup of an actual screen with one line under it.
 *
 * Every claim describes something shipped and verified — see README.md. Where
 * something genuinely is not built (mobile money, a live SMS provider), the
 * page says so instead of going quiet: the buyer finds out in week one either
 * way.
 *
 * On colour: the app's own screens are nearly monochrome, because a dense fee
 * table is unreadable when every column shouts. The brand palette behind them
 * is not, and this is where it gets used at full strength. Mint, coral and
 * gold are light enough that they carry ink text rather than paper — paper on
 * them lands around 3:1, which fails AA at these sizes.
 */

const UGX = (amount: number): Money => ({ amount: BigInt(amount), currency: "UGX" });
const ugx = (amount: number, symbol = false): string => formatMoney(UGX(amount), { symbol });

/**
 * One P.7 boarder's Term III account, from a real fee structure: tuition
 * 620,000 + boarding 380,000 + development 50,000 + examinations 45,000 + PLE
 * registration 60,000.
 *
 * At module scope rather than inside the component so the reveal interval has
 * nothing to depend on.
 */
const HERO_ROWS: readonly { date: string; detail: string; debit?: number; credit?: number }[] = [
  { date: "16 Sep", detail: "Term III fees, P.7 boarding", debit: 1_155_000 },
  { date: "18 Sep", detail: "Bursary, 25% of tuition", credit: 155_000 },
  { date: "24 Sep", detail: "Payment, mobile money", credit: 400_000 },
  { date: "09 Oct", detail: "Payment, cash", credit: 250_000 },
];

/**
 * The admin portal's own sidebar, item for item and icon for icon — see
 * `AdminPortal.tsx`. It is in the hero because the ledger alone sells one
 * module: a director looking at a fee statement cannot tell whether the other
 * six things they need exist. The sidebar answers that in the same glance,
 * and it can be copied from the real portal rather than invented because the
 * real portal is what it is a picture of.
 */
const HERO_NAV: readonly { label: string; icon: LucideIcon }[] = [
  { label: "Overview", icon: LayoutDashboard },
  { label: "Students", icon: Users },
  { label: "Staff", icon: UserCog },
  { label: "Classes", icon: GraduationCap },
  { label: "Calendar", icon: Calendar },
  { label: "Timetable", icon: CalendarClock },
  { label: "SMS", icon: MessageSquare },
];

// ─────────────────────────────────────────────────────────────────────

export function LandingPage() {
  return (
    <SitePage tone="midnight">
      <Hero />
      <WhoFor />
      <HomepageIllustrations />
      <Modules />
      <InsideTheApp />
      <Isolation />
      <EastAfrica />
      <PricingPreview />
      <Honesty />
      <Closing />
    </SitePage>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Illustrations
// ─────────────────────────────────────────────────────────────────────

function HomepageIllustrations() {
  const stories = [
    {
      image: "/images/home/illustrations/school-team.png",
      alt: "A school team collaborating around a tablet",
      eyebrow: "Everyone connected",
      title: "One view for the whole school team.",
      body: "Directors, bursars, teachers and parents each get the part of WakaSkul they need.",
    },
    {
      image: "/images/home/illustrations/fees-ledger.png",
      alt: "A parent and bursar reviewing a fee receipt and ledger",
      eyebrow: "Fees that add up",
      title: "Every payment has a place.",
      body: "Receipts, balances and statements stay tied to the ledger, so the numbers are easy to trust.",
    },
    {
      image: "/images/home/illustrations/classroom-learning.png",
      alt: "A teacher guiding two students through a classroom exercise",
      eyebrow: "Learning in motion",
      title: "The classroom stays in the picture.",
      body: "Attendance, marks and report cards move with the students and the teachers who know them.",
    },
    {
      image: "/images/home/illustrations/school-day-coordination.png",
      alt: "A teacher coordinating a timetable and parent messages",
      eyebrow: "A clearer school day",
      title: "From timetable to parent message.",
      body: "The daily details sit together, making it easier to coordinate people, time and communication.",
    },
  ] as const;

  return (
    <section className="border-b border-rule bg-paper">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:py-20">
        <SectionHead
          eyebrow="Designed around real school work"
          title="The pieces fit together."
          lede="WakaSkul brings the people, money, learning and daily coordination of a school into one calm system."
          figure={{ value: "4", label: "connected ways to work" }}
        />

        <div className="mt-12 grid gap-4 md:grid-cols-2">
          {stories.map((story) => (
            <article key={story.title} className="overflow-hidden rounded border border-rule bg-raised">
              <div className="flex h-52 items-end justify-center overflow-hidden bg-accent-soft/25 px-6 pt-5 sm:h-60">
                <img
                  src={story.image}
                  alt={story.alt}
                  className="h-full w-full object-contain object-bottom"
                  loading="lazy"
                />
              </div>
              <div className="border-t border-rule px-5 py-5">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-accent">
                  {story.eyebrow}
                </p>
                <h3 className="mt-2 font-display text-xl font-bold leading-snug text-ink">
                  {story.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft">{story.body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Hero
// ─────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-rule">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: "url('/images/home/hero-school-campus.png')" }}
      />
      <div
        aria-hidden="true"
        className="site-hero-overlay pointer-events-none absolute inset-0 opacity-70"
      />

      <div className="relative mx-auto max-w-6xl px-4 py-14 lg:py-20">
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.3fr] lg:gap-14">
          <div>
            {/* The brand lockup sits above the headline rather than inside it.
                A visitor who lands here cold has to learn the product's name,
                and the small logo in the header is not where their eye goes
                first; the boxed "Waka" is also the one piece of brand drawing
                on the page, so it is worth its own line. The thesis stays the
                h1 underneath, because that is the sentence the page argues. */}
            <OfficialWordmark className="h-20 sm:h-24" dark />
            <div className="mt-3">
              <Eyebrow>School management, Uganda</Eyebrow>
            </div>
            <h1 className="display-hero-split mt-6 font-display font-bold text-ink">
              Every shilling,
              <br />
              <span className="text-accent">accounted for.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-ink-soft">
              Fees, students, marks, timetable and parent SMS in one system, one school at a time.
              Every balance derived from its ledger, never a number somebody typed.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <PrimaryAction to="/demo">
                Explore the live demo
                <span aria-hidden="true">→</span>
              </PrimaryAction>
              <SecondaryAction to="/#pricing">See pricing</SecondaryAction>
            </div>

            <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-soft">
              {["No sign-up needed", "Real screens, real arithmetic", "Nothing to install"].map(
                (item) => (
                  <li key={item} className="flex items-center gap-1.5">
                    <Check className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                    {item}
                  </li>
                ),
              )}
            </ul>
          </div>

          <HeroDashboard />
        </div>
      </div>
    </section>
  );
}

/**
 * The hero screen: a director's overview, in browser chrome.
 *
 * The one live thing on the page is the balance — the rows arrive one at a
 * time and it is recomputed from whichever are present, because that is
 * literally what the product does on every read.
 */
function HeroDashboard() {
  // Show the real balance immediately. A marketing hero should never flash a
  // misleading zero while its decorative reveal animation catches up.
  const visible = HERO_ROWS.length;

  const balance = HERO_ROWS
    .slice(0, visible)
    .reduce((running, r) => running + (r.debit ?? 0) - (r.credit ?? 0), 0);

  return (
    <BrowserFrame url="kigo-hill.wakaskul.com/admin">
      <div className="flex">
        <HeroSidebar />

        <div className="flex min-w-0 flex-1 flex-col gap-3 p-3.5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-[0.55rem] uppercase tracking-[0.16em] text-accent">
                Kigo Hill Primary School
              </p>
              <p className="font-display text-sm font-bold text-ink">Term III 2026 · Wakiso</p>
            </div>
            <span className="flex shrink-0 items-center gap-1.5 rounded bg-good/12 px-2 py-1 text-[0.6rem] font-medium text-good">
              <span className="h-1.5 w-1.5 rounded-full bg-good" aria-hidden="true" />
              180 on roll
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Tile label="Collected" value={ugx(112_119_000)} tone="text-good" />
            <Tile label="Outstanding" value={ugx(54_561_000)} tone="text-bad" />
            <Tile label="In class" value="167/180" tone="text-ink" />
          </div>

          <div className="rounded border border-rule bg-paper">
            <p className="truncate border-b border-rule-soft px-2.5 py-2 font-mono text-[0.55rem] uppercase tracking-[0.16em] text-ink-faint">
              Nakato Grace · KHP/2026/0412 · P.7 East
            </p>
            <table className="w-full text-[0.7rem]">
              <caption className="sr-only">A learner&apos;s fee statement</caption>
              <tbody className="tabular">
                {HERO_ROWS.map((row, i) => (
                  <tr
                    key={row.detail}
                    className="border-b border-rule-soft transition-opacity duration-500"
                    style={{ opacity: i < visible ? 1 : 0 }}
                    aria-hidden={i >= visible}
                  >
                    <td className="whitespace-nowrap px-2.5 py-1.5 text-ink-faint">{row.date}</td>
                    <td className="px-2.5 py-1.5 text-ink-soft">{row.detail}</td>
                    <td
                      className={`whitespace-nowrap px-2.5 py-1.5 text-right font-medium ${
                        row.credit ? "text-good" : "text-bad"
                      }`}
                    >
                      {row.credit ? "−" : "+"}
                      {ugx(row.credit ?? row.debit ?? 0)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-highlight-soft">
                  <td
                    className="px-2.5 py-2 font-mono text-[0.55rem] uppercase tracking-[0.14em] text-ink"
                    colSpan={2}
                  >
                    Balance
                  </td>
                  <td className="tabular whitespace-nowrap px-2.5 py-2 text-right text-sm font-bold text-ink">
                    {ugx(balance, true)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-[0.65rem] text-ink-faint">
            <code className="font-mono">SUM(debit) − SUM(credit)</code>, recomputed as each row
            lands. Nothing stores it.
          </p>
        </div>
      </div>
    </BrowserFrame>
  );
}

/**
 * A picture of the admin sidebar, not a working one — nothing here navigates,
 * so it is a list rather than a `nav` of dead links. The labels stay real text
 * (a screen reader gets the module names) while the icons are decorative, the
 * same split the real shell makes. Below `sm` the labels drop and the rail
 * goes to icons only, because a phone-width hero cannot afford 9rem of chrome.
 */
function HeroSidebar() {
  return (
    <div className="flex shrink-0 flex-col border-r border-rule bg-raised py-3">
      <div className="mb-2 px-2.5 sm:px-3">
        <p className="font-display text-xs font-bold leading-none text-ink">
          <span className="hidden sm:inline">Wakaskul</span>
          <span className="sm:hidden" aria-hidden="true">
            W
          </span>
        </p>
        <p className="mt-1 hidden font-mono text-[0.5rem] uppercase tracking-[0.16em] text-accent sm:block">
          Administration
        </p>
      </div>

      <ul className="flex flex-col gap-0.5 px-1.5">
        {HERO_NAV.map((item, i) => (
          <li
            key={item.label}
            className={`flex items-center gap-2 rounded px-2 py-1.5 text-[0.7rem] ${
              i === 0 ? "bg-highlight-soft font-medium text-ink" : "text-ink-soft"
            }`}
          >
            <item.icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="rounded border border-rule bg-paper px-2.5 py-2">
      <p className="font-mono text-[0.5rem] uppercase tracking-[0.14em] text-ink-faint">{label}</p>
      <p className={`tabular mt-1 text-[0.8rem] font-bold ${tone}`}>{value}</p>
    </div>
  );
}

/** A window, so a mockup reads as a screenshot of software rather than a card. */
function BrowserFrame({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border border-rule bg-raised shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)]">
      <div className="flex items-center gap-2 border-b border-rule bg-sunk px-3 py-2">
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="h-2 w-2 rounded-full bg-bad/60" />
          <span className="h-2 w-2 rounded-full bg-warn/60" />
          <span className="h-2 w-2 rounded-full bg-good/60" />
        </span>
        <span className="truncate rounded bg-paper px-2 py-0.5 font-mono text-[0.6rem] text-ink-faint">
          {url}
        </span>
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Who it is for
// ─────────────────────────────────────────────────────────────────────

function WhoFor() {
  const cards: readonly {
    role: string;
    route: string;
    skin: string;
    items: readonly string[];
  }[] = [
    {
      role: "Director",
      route: "/admin",
      skin: "border-accent/35 bg-accent/8",
      items: ["Roll, enrolment, promotion", "Spreadsheet import", "Collection by class", "Audit log"],
    },
    {
      role: "Bursar",
      route: "/admin",
      skin: "border-good/35 bg-good/8",
      items: ["Fee structures", "Invoices & receipts", "Append-only ledger", "Thermal printing"],
    },
    {
      role: "Teacher",
      route: "/teach",
      skin: "border-warn/35 bg-highlight-soft/30",
      items: ["Daily register", "Marks entry", "Positions", "Own classes only"],
    },
    {
      role: "Parent",
      route: "/parent",
      skin: "border-bad/35 bg-bad/8",
      items: ["Fee balance", "Full statement", "Attendance", "Report card"],
    },
  ];

  return (
    <section className="border-b border-rule bg-raised">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:py-20">
        <SectionHead
          eyebrow="Built for everyone at the school"
          title="One system. Four portals."
          figure={{ value: "4", label: "shells over one API" }}
        />

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((card) => (
            <div key={card.role} className={`rounded-lg border ${card.skin} p-4`}>
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="font-display text-lg font-bold text-ink">{card.role}</h3>
                <code className="font-mono text-[0.6rem] text-ink-faint">{card.route}</code>
              </div>
              <ul className="mt-3 flex flex-col gap-1.5 text-sm text-ink-soft">
                {card.items.map((item) => (
                  <li key={item} className="flex gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Modules
// ─────────────────────────────────────────────────────────────────────

/**
 * The module map. Every name here is a screen or endpoint that exists. Cards
 * make the scope scannable without turning the landing page into a wall of
 * undifferentiated chips.
 */
function Modules() {
  const groups: readonly {
    name: string;
    description: string;
    skin: string;
    tile: string;
    icon: LucideIcon;
    items: readonly string[];
  }[] = [
    {
      name: "People",
      description: "The people and records behind every school day.",
      skin: "text-accent",
      tile: "border-[#156064] bg-[#156064] text-[#f6f8f0] hover:bg-[#0e4e4d]",
      icon: Users,
      items: [
        "Students", "Guardians", "Staff", "Classes", "Streams", "Subjects",
        "Enrolment", "Promotion", "Spreadsheet import", "Qualifications", "Assignments",
      ],
    },
    {
      name: "Fees",
      description: "A clean trail from fee structure to receipt.",
      skin: "text-good",
      tile: "border-[#f6c32f] bg-[#f6c32f] text-[#10151f] hover:bg-[#e6b41f]",
      icon: CircleDollarSign,
      items: [
        "Fee items", "Fee structures", "Discounts", "Bursaries", "Invoices",
        "Payments", "Receipts", "Ledger", "Statements", "Arrears",
      ],
    },
    {
      name: "Academics",
      description: "Marks, grading and reports in one academic record.",
      skin: "text-warn",
      tile: "border-[#c9f76f] bg-[#c9f76f] text-[#10151f] hover:bg-[#b9e85f]",
      icon: GraduationCap,
      items: [
        "Attendance", "Assessments", "Marks", "Grading schemes", "Grade bands",
        "Aggregates", "Divisions", "Positions", "Report cards", "Results",
      ],
    },
    {
      name: "Timetable",
      description: "Put the right class, teacher and room together.",
      skin: "text-bad",
      tile: "border-[#ff8b6a] bg-[#ff8b6a] text-[#10151f] hover:bg-[#ef795a]",
      icon: CalendarClock,
      items: [
        "Period structures", "Rooms", "Versions", "Slot placement", "Conflict checks",
        "Teaching load", "Substitutions",
      ],
    },
    {
      name: "Messaging",
      description: "Reach parents with the right message and cost.",
      skin: "text-accent",
      tile: "border-[#156064] bg-[#156064] text-[#f6f8f0] hover:bg-[#0e4e4d]",
      icon: MessageSquare,
      items: ["Templates", "Campaigns", "Audiences", "Cost preview", "Credit ledger", "Send queue"],
    },
    {
      name: "Platform",
      description: "The controls that keep the whole system dependable.",
      skin: "text-good",
      tile: "border-[#c9f76f] bg-[#c9f76f] text-[#10151f] hover:bg-[#b9e85f]",
      icon: Settings2,
      items: [
        "Tenants", "Module toggles", "Academic calendar", "Audit log", "Number sequences",
        "Desktop till", "Parent portal",
      ],
    },
  ];

  return (
    <section id="modules" className="border-b border-rule bg-paper">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:py-20">
        <SectionHead
          eyebrow="Modules"
          title="Everything a school actually runs on."
          lede="Each of these is a switch on your school rather than a separate product. Turn off what you do not use and it leaves the sidebar."
          figure={{
            // Counted from the list below rather than typed, so the claim
            // cannot drift away from the list that substantiates it.
            value: String(groups.reduce((n, g) => n + g.items.length, 0)),
            label: `features, grouped ${groups.length} ways`,
          }}
        />

        <div className="mt-12 grid gap-4 md:grid-cols-2">
          {groups.map((group) => (
            <article key={group.name} className="rounded border border-rule bg-raised p-5 shadow-[0_10px_28px_rgba(18,32,31,0.05)]">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded bg-paper ${group.skin}`}>
                    <group.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-bold text-ink">{group.name}</h3>
                    <p className="mt-1 max-w-sm text-sm leading-relaxed text-ink-soft">{group.description}</p>
                  </div>
                </div>
                <span className="font-display text-2xl font-bold text-ink-faint" aria-label={`${group.items.length} features`}>
                  {String(group.items.length).padStart(2, "0")}
                </span>
              </div>
              <ul className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {group.items.map((item) => (
                  <li
                    key={item}
                    className={`flex min-h-12 items-center gap-2 rounded border px-2.5 py-2 text-xs font-semibold shadow-[0_5px_14px_rgba(0,0,0,0.12)] transition hover:-translate-y-0.5 ${group.tile}`}
                  >
                    <Layers3 className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Inside the app
// ─────────────────────────────────────────────────────────────────────

/** Three real screens, each with one line under it. */
function InsideTheApp() {
  return (
    <section id="inside" className="border-b border-rule bg-raised">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:py-20">
        <SectionHead
          eyebrow="Inside the app"
          title="This is the actual software."
          lede="Not an illustration of it. These are the screens a teacher, a director of studies and a bursar use, with the behaviour described underneath each one."
          figure={{ value: "3", label: "screens, of twelve in the demo" }}
        />

        <div className="mt-14 flex flex-col gap-14">
          <Screen
            url="kigo-hill.wakaskul.com/teach/register"
            title="The register takes ten seconds"
            caption="Present, late, absent, excused — four real states, not a checkbox that loses the difference. It is what the parent portal reads and what an absence SMS is built from."
          >
            <RegisterMock />
          </Screen>

          <Screen
            url="kigo-hill.wakaskul.com/admin/timetable"
            title="Nobody gets double-booked"
            caption="A class, a teacher or a room in two places in one period is refused — as a database constraint, not only a check in code. An unqualified teacher warns, and is allowed."
            reverse
          >
            <TimetableMock />
          </Screen>

          <Screen
            url="kigo-hill.wakaskul.com/admin/campaigns/new"
            title="The cost is shown before it sends"
            caption="A broadcast reaches a parent with three children once. A reminder naming each child's balance correctly sends three, rather than silently dropping two."
          >
            <SmsMock />
          </Screen>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 rounded border-2 border-accent px-6 py-3 font-display font-bold text-accent hover:bg-accent hover:text-paper"
          >
            Open these screens in the demo
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Screen({
  url,
  title,
  caption,
  reverse,
  children,
}: {
  url: string;
  title: string;
  caption: string;
  reverse?: boolean;
  children: React.ReactNode;
}) {
  return (
    <article className="grid items-center gap-8 lg:grid-cols-[1.3fr_1fr] lg:gap-12">
      <div className={reverse ? "lg:order-2" : undefined}>
        <BrowserFrame url={url}>{children}</BrowserFrame>
      </div>
      <div className={reverse ? "lg:order-1" : undefined}>
        <h3 className="font-display text-2xl font-bold leading-snug tracking-tight text-ink">
          {title}
        </h3>
        <p className="mt-3 leading-relaxed text-ink-soft">{caption}</p>
      </div>
    </article>
  );
}

function RegisterMock() {
  const roll = [
    { name: "Achieng Sarah", status: "Present", skin: "bg-good text-ink" },
    { name: "Byaruhanga Ivan", status: "Late", skin: "bg-warn text-ink" },
    { name: "Kansiime Faith", status: "Present", skin: "bg-good text-ink" },
    { name: "Okello Moses", status: "Absent", skin: "bg-bad text-ink" },
    { name: "Waiswa Denis", status: "Excused", skin: "bg-accent text-paper" },
    { name: "Nakato Grace", status: "Present", skin: "bg-good text-ink" },
  ];

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="font-display text-sm font-bold text-ink">P.7 East · Thu 15 Oct</p>
        <p className="tabular text-[0.65rem] text-ink-faint">25 present · 2 late · 1 absent</p>
      </div>
      <ul className="flex flex-col gap-1.5">
        {roll.map((pupil) => (
          <li
            key={pupil.name}
            className="flex items-center justify-between gap-3 rounded border border-rule bg-paper px-2.5 py-1.5 text-xs"
          >
            <span className="text-ink">{pupil.name}</span>
            <span className={`rounded px-2 py-0.5 text-[0.6rem] font-medium ${pupil.skin}`}>
              {pupil.status}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TimetableMock() {
  const grid: readonly (readonly { code: string; skin: string }[])[] = [
    [
      { code: "MTC", skin: "bg-accent text-paper" },
      { code: "ENG", skin: "bg-good text-ink" },
      { code: "SCI", skin: "bg-warn text-ink" },
      { code: "SST", skin: "bg-bad text-ink" },
      { code: "KIS", skin: "bg-accent text-paper" },
    ],
    [
      { code: "ENG", skin: "bg-good text-ink" },
      { code: "MTC", skin: "bg-accent text-paper" },
      { code: "SST", skin: "bg-bad text-ink" },
      { code: "", skin: "" },
      { code: "SCI", skin: "bg-warn text-ink" },
    ],
    [
      { code: "SCI", skin: "bg-warn text-ink" },
      { code: "SST", skin: "bg-bad text-ink" },
      { code: "MTC", skin: "bg-accent text-paper" },
      { code: "ENG", skin: "bg-good text-ink" },
      { code: "RE", skin: "bg-sunk text-ink-soft" },
    ],
  ];

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="font-display text-sm font-bold text-ink">P.7 East · Term III</p>
        <span className="rounded bg-good/12 px-2 py-0.5 text-[0.6rem] font-medium text-good">
          Published
        </span>
      </div>

      <div className="grid grid-cols-[2.2rem_repeat(5,1fr)] gap-1 text-center">
        <span />
        {["Mon", "Tue", "Wed", "Thu", "Fri"].map((d) => (
          <span key={d} className="font-mono text-[0.55rem] uppercase tracking-[0.1em] text-ink-faint">
            {d}
          </span>
        ))}

        {grid.map((row, r) => (
          <div key={r} className="contents">
            <span className="self-center font-mono text-[0.55rem] text-ink-faint">{r + 1}</span>
            {row.map((cell, c) => (
              <span
                key={`${r}-${c}`}
                className={`rounded py-2 text-[0.6rem] font-medium ${
                  cell.code ? cell.skin : "border border-dashed border-rule text-ink-faint"
                }`}
              >
                {cell.code || "+"}
              </span>
            ))}
          </div>
        ))}
      </div>

      <p className="mt-3 rounded border-l-2 border-l-bad bg-bad/8 py-1.5 pl-2.5 text-[0.65rem] text-ink">
        Refused: this teacher is already teaching another class in this period.
      </p>
    </div>
  );
}

function SmsMock() {
  return (
    <div className="p-4">
      <div className="mb-3 grid grid-cols-3 gap-2">
        <Tile label="Recipients" value="99" tone="text-ink" />
        <Tile label="Segments" value="112" tone="text-ink" />
        <Tile label="Cost" value={ugx(3_584, true)} tone="text-accent" />
      </div>

      <p className="rounded-lg rounded-tl-none border border-rule bg-paper px-3 py-2.5 text-xs leading-relaxed text-ink">
        Dear Parent, the fees balance for Nakato Grace (P.7 East) is UGX 350,000. Kindly clear
        before end of term. Kigo Hill Primary School
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[0.65rem]">
        <span className="rounded bg-good/12 px-2 py-0.5 font-medium text-good">1 segment</span>
        <span className="rounded bg-sunk px-2 py-0.5 text-ink-soft">GSM-7</span>
        <span className="rounded bg-accent/12 px-2 py-0.5 font-medium text-accent">
          4,820 credits left
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Isolation
// ─────────────────────────────────────────────────────────────────────

/**
 * The multi-tenancy answer. For a director evaluating shared software, "can
 * the school down the road see my fee records" is the whole question.
 */
function Isolation() {
  return (
    <section className="border-b border-rule bg-ink">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:py-20">
        <SectionHead
          eyebrow="One deployment, many schools"
          title={
            <>
              Isolated <span className="text-highlight-soft">twice over</span>.
            </>
          }
          tone="paper"
          figure={{ value: "41", label: "tables under row-level security" }}
        />
        <p className="mt-5 max-w-xl text-lg leading-relaxed text-paper/80">
          Your school gets its own address and its own records.{" "}
          <code className="font-mono text-base text-highlight-soft">
            yourschool.wakaskul.com
          </code>
        </p>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <Layer
            n="Layer one"
            accent="text-good"
            title="Scoped in the application"
            body="Every read and write carries the school it belongs to, added automatically. No screen has to remember to filter."
          />
          <Layer
            n="Layer two"
            accent="text-highlight-soft"
            title="Refused by the database"
            body="Row-level security enforces the same boundary underneath. If layer one has a bug, a cross-school read returns nothing."
          />
          <Layer
            n="And it cannot lapse"
            accent="text-accent-soft"
            title="Proven on every build"
            body="A test inspects the live schema and fails the build if a new table of school records is missing its policy."
          />
        </div>
      </div>
    </section>
  );
}

function Layer({
  n,
  title,
  body,
  accent,
}: {
  n: string;
  title: string;
  body: string;
  accent: string;
}) {
  return (
    <div className="rounded-lg border border-paper/20 bg-paper/5 px-4 py-4">
      <p className={`font-mono text-[0.55rem] uppercase tracking-[0.16em] ${accent}`}>{n}</p>
      <h3 className="mt-2 font-display text-lg font-bold text-paper">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-paper/75">{body}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  East Africa
// ─────────────────────────────────────────────────────────────────────

function EastAfrica() {
  const items = [
    { title: "UGX-native", body: "Whole shillings, never a float. Money is an integer everywhere." },
    { title: "PLE-style grading", body: "Aggregates, divisions and positions — configured, not hardcoded." },
    { title: "Day-first dates", body: "And a spreadsheet import that reads the sheet you already keep." },
    { title: "Parent SMS", body: "The channel that reaches a Ugandan parent, with cost shown first." },
  ];

  return (
    <section className="border-b border-rule bg-accent-soft">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:py-20">
        <SectionHead
          eyebrow="Rooted in Uganda"
          title="Built for how schools here actually work."
          figure={{ value: "UGX", label: "the only currency assumed" }}
        />

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <div key={item.title} className="border-t-2 border-t-accent pt-3">
              <h3 className="font-display text-base font-bold text-ink">{item.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Pricing
// ─────────────────────────────────────────────────────────────────────

function PricingPreview() {
  const plans = [
    {
      name: "Essential",
      audience: "For small schools getting organised",
      price: "UGX 1.35m",
      unit: "/ year",
      setup: "UGX 750k one-time setup",
      features: ["Up to 200 learners", "Fees and ledger", "Roll, attendance and marks", "Parent portal"],
      accent: "border-rule bg-paper text-ink",
      action: "Start with Essential",
    },
    {
      name: "Growth",
      audience: "For schools ready to run one clear system",
      price: "UGX 2.25m",
      unit: "/ year",
      setup: "UGX 1.5m one-time setup",
      features: ["201–800 learners", "Everything in Essential", "Timetable and report cards", "Parent SMS and staff roles"],
      accent: "border-accent bg-accent text-paper",
      action: "Choose Growth",
      popular: true,
    },
    {
      name: "Pro",
      audience: "For larger schools and deeper controls",
      price: "UGX 3.0m",
      unit: "/ year",
      setup: "From UGX 2.5m one-time setup",
      features: ["801+ learners", "Everything in Growth", "Advanced finance and reporting", "Priority onboarding and support"],
      accent: "border-ink bg-ink text-paper",
      action: "Talk about Pro",
    },
  ];

  return (
    <section id="pricing" className="scroll-mt-20 border-b border-rule bg-raised">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:py-20">
        <SectionHead
          eyebrow="Simple pricing"
          title="Three plans. One clearer school year."
          lede="Start with the essentials and grow into deeper finance, reporting and support when your school is ready."
          figure={{ value: "3", label: "plans, billed annually" }}
        />

        <div className="mt-12 grid gap-4 lg:grid-cols-3">
          {plans.map((plan) => (
            <article key={plan.name} className={`relative flex flex-col rounded-lg border p-5 ${plan.accent}`}>
              {plan.popular && (
                <span className="absolute -top-3 left-5 rounded bg-highlight px-2.5 py-1 font-mono text-[0.55rem] font-bold uppercase tracking-[0.16em] text-ink">
                  Most popular
                </span>
              )}
              <p className={`font-mono text-[0.6rem] uppercase tracking-[0.18em] ${plan.popular ? "text-paper/70" : plan.name === "Pro" ? "text-paper/60" : "text-accent"}`}>
                {plan.name}
              </p>
              <h3 className="mt-3 font-display text-2xl font-bold">{plan.audience}</h3>
              <div className="mt-8 flex items-baseline gap-2">
                <span className="font-display text-3xl font-bold">{plan.price}</span>
                <span className={`text-sm ${plan.popular || plan.name === "Pro" ? "text-paper/65" : "text-ink-soft"}`}>{plan.unit}</span>
              </div>
              <p className={`mt-2 text-sm ${plan.popular || plan.name === "Pro" ? "text-paper/70" : "text-ink-soft"}`}>{plan.setup}</p>
              <ul className={`mt-8 grid gap-3 border-t pt-5 text-sm ${plan.popular || plan.name === "Pro" ? "border-paper/20 text-paper/80" : "border-rule text-ink-soft"}`}>
                {plan.features.map((feature) => (
                  <li key={feature} className="flex gap-2">
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${plan.popular || plan.name === "Pro" ? "text-highlight" : "text-accent"}`} aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link to="/onboarding" className={`mt-8 inline-flex items-center justify-between rounded px-4 py-3 font-display font-bold ${plan.popular ? "bg-highlight text-ink" : plan.name === "Pro" ? "bg-paper text-ink" : "border-2 border-ink text-ink hover:bg-ink hover:text-paper"}`}>
                {plan.action}<span aria-hidden="true">→</span>
              </Link>
            </article>
          ))}
        </div>

        <p className="mt-6 text-sm leading-relaxed text-ink-soft">
          Setup includes onboarding and configuration. SMS gateway, mobile-money and custom data migration costs are quoted separately. <Link to="/pricing" className="font-medium text-accent hover:underline">See full pricing details</Link>.
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Honesty
// ─────────────────────────────────────────────────────────────────────

/**
 * The section a landing page usually leaves out. This audience finds out in
 * week one regardless, and saying it here is what makes the rest credible.
 */
function Honesty() {
  return (
    <section className="border-b border-rule bg-paper">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:py-20">
        <SectionHead
          eyebrow="Straight answers"
          title="What is running, and what is not."
          lede="Set in the same size type as the features, because you find out in week one either way and would rather hear it now."
          figure={{ value: "5", label: "things deliberately not built yet" }}
        />

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-good/35 bg-good/8 p-5">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-good">Built</p>
            <ul className="mt-4 grid gap-2 text-sm text-ink-soft sm:grid-cols-2">
              {[
                "Roll & enrolment",
                "Spreadsheet import",
                "Fees & ledger",
                "Invoices & receipts",
                "Attendance",
                "Marks & grading",
                "Report cards",
                "Timetable",
                "Parent SMS",
                "Parent portal",
                "Desktop till",
                "Thermal receipts",
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-good" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-warn/35 bg-highlight-soft/30 p-5">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-warn">Not yet</p>
            <ul className="mt-4 flex flex-col gap-2.5 text-sm leading-relaxed text-ink-soft">
              {[
                "Mobile money collection — no MoMo or Airtel credentials yet, so the bursar records the payment.",
                "A live SMS provider — everything up to handing the message over is built and running.",
                "A native mobile app — the web app installs to a phone and works offline.",
                "PDF report cards — they print from the browser today.",
                "A full term at a real school. Yours could be the first.",
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden="true" className="font-bold text-warn">
                    ·
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Closing() {
  return (
    <section className="bg-highlight-soft">
      <div className="mx-auto max-w-6xl px-4 py-16 text-center lg:py-24">
        <h2 className="display-xl mx-auto max-w-3xl font-display font-bold text-ink">
          Look around a whole school first.
        </h2>
        <p className="mx-auto mt-5 max-w-lg text-lg leading-relaxed text-ink-soft">
          180 learners, a term of fees, marks, a timetable and parent messages. Record a payment,
          enter a mark, place a lesson. No sign-in, and nothing you do reaches a real school.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 rounded bg-accent px-6 py-3.5 font-display font-bold text-paper hover:opacity-90"
          >
            Explore the live demo
            <span aria-hidden="true">→</span>
          </Link>
          <a
            href="tel:+256773550009"
            className="rounded border-2 border-ink px-6 py-3.5 font-display font-bold text-ink hover:bg-ink hover:text-paper"
          >
            +256 773 550009
          </a>
        </div>
      </div>
    </section>
  );
}
